import { onlineManager } from '@tanstack/react-query';

/**
 * Есть ли у устройства сеть - по мнению браузера (`navigator.onLine`, события `online`/`offline`).
 *
 * Это не "дойдёт ли запрос до сервера": `true` бывает и за мёртвым Wi-Fi. Зато `false` значит
 * ровно "подключения нет", и на нём держится всё, что говорит человеку про сеть: полоса
 * "Нет подключения" и молчание тостов об ошибках чтения, пока она на экране.
 *
 * На сервере и в среде без `navigator` сеть считается есть: там показывать некому.
 */
export const isOnline = (): boolean =>
  typeof navigator === 'undefined' || navigator.onLine !== false;

/** Подписка для `useSyncExternalStore`. */
export const subscribeOnline = (onChange: () => void): (() => void) => {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);

  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
};

let offlineAtBoot: boolean | null = null;

/**
 * Приложение, старт: сеть - такой, какой её видит WebView, и до первого запроса.
 *
 * react-query начинает с "сеть есть" и узнаёт о пропаже только из события `offline`. Холодный
 * старт в авиарежиме такого события не получает: каждый запрос делал три попытки и через ~7 с
 * падал ошибкой. С этим вызовом запросы встают на паузу (`fetchStatus: 'paused'`) с данными
 * снимка на экране и продолжаются сами, когда сеть вернётся.
 *
 * Только в приложении. В Android WebView флаг идёт от ConnectivityManager (есть сеть по умолчанию
 * с доступом в интернет) и ложным "нет" не залипает. В вебе react-query нарочно не читает
 * `navigator.onLine` на старте: у настольного Chromium бывают ложные "нет сети", а у анонимного
 * посетителя нет ни сокета, ни мутаций, которые сняли бы паузу (`markNetworkProven`). Вебу хватает
 * `installReconnectCatchUp`.
 *
 * Только "нет": "есть" - и так значение по умолчанию.
 */
export const syncOnlineAtBoot = (): void => {
  offlineAtBoot = !isOnline();
  if (offlineAtBoot) {
    onlineManager.setOnline(false);
  }
};

/** Был ли старт без сети; `null` - старт этого не проверял (веб). Для метрик старта. */
export const wasOfflineAtBoot = (): boolean | null => offlineAtBoot;

/*
  Сервер недоступен, хотя браузер говорит "сеть есть".

  Так бывает с постоянным VPN: в авиарежиме Android оставляет сеть VPN с признаками "интернет
  есть, проверен", а под ней ничего нет, и WebView честно отвечает `navigator.onLine === true`.
  Полосы не было, чтения молча падали, а на экране висели данные из снимка, будто всё в порядке.
  Тот же случай - мёртвый Wi-Fi и закрытая сеть без проверки.

  Поэтому признак ставится по самим запросам: два провала подряд без ответа (статус 0, не ошибка
  HTTP) и ни одного ответа между ними. Провалы запросов, ушедших до уже засчитанного провала, -
  тот же самый обрыв: пачка одновременных запросов, упавших вместе, считается за один, и
  единичный сбой полосу не показывает. Снимает признак любое доказательство живой сети
  (`markNetworkProven`), проверку раз в 10-30 с ведёт `installReachProbe`.
*/

/** Сколько провалов подряд нужно, чтобы признать сервер недоступным. */
export const UNREACHABLE_AFTER_FAILURES = 2;

/**
 * Кто перепроверяет связь, когда ответ не пришёл вовремя. Ставит `installReachProbe`.
 *
 * Через сеттер, а не импортом: проверка сама ходит через `gqlFetcher`, который зовёт отсюда
 * `markNetworkFailure`, - прямой импорт замкнул бы круг.
 */
let verifyReach: (() => void) | null = null;

export const setReachVerifier = (verifier: (() => void) | null): void => {
  verifyReach = verifier;
};

/** Не чаще раза в столько: медленный шлюз роняет по таймауту сразу пачку запросов. */
const VERIFY_THROTTLE_MS = 10_000;
let lastVerifyAt = 0;

let serverUnreachable = false;
let failuresInRow = 0;
let lastFailureAt: number | null = null;
const reachListeners = new Set<() => void>();

const setServerUnreachable = (value: boolean): void => {
  if (serverUnreachable === value) {
    return;
  }
  serverUnreachable = value;
  for (const listener of reachListeners) {
    listener();
  }
};

/** Сервер не отвечает при "сети есть" (см. выше). */
export const isServerUnreachable = (): boolean => serverUnreachable;

/** Подписка для `useSyncExternalStore`. */
export const subscribeServerReach = (onChange: () => void): (() => void) => {
  reachListeners.add(onChange);
  return () => {
    reachListeners.delete(onChange);
  };
};

/**
 * Связи нет: браузер знает, что сети нет, или сервер не отвечает. На этом держится молчание
 * тостов об ошибках чтения - о причине уже говорит полоса.
 */
export const hasConnectionTrouble = (): boolean => !isOnline() || serverUnreachable;

/**
 * Запрос к шлюзу не получил ответа. `startedAt` - когда он ушёл, мс.
 *
 * Когда браузер и сам знает, что сети нет, провал ничего не добавляет: полоса уже про это. Когда
 * сервер признан недоступным, чтения встают на паузу, как без сети, - и снова встают, если
 * событие `online` успело их отпустить.
 */
export const markNetworkFailure = (startedAt: number, timedOut = false): void => {
  if (!isOnline() || (lastFailureAt !== null && startedAt <= lastFailureAt)) {
    return;
  }

  /*
    🔴 «Долго отвечает» - это не «не отвечает». Пока разницы не было, медленный шлюз объявлялся
    упавшим: два чтения подряд упирались в свои 20 секунд, под шапкой появлялась полоса «Нет
    связи с сервером», экран замирал, проверка связи отвечала мгновенно, полоса гасла - и сайт
    тут же перезапрашивал всё, что открыто. Дальше круг повторялся примерно раз в 45 секунд у
    каждого открытого клиента, и тем сильнее, чем хуже серверу.

    Теперь таймаут в счёт не идёт вовсе: он говорит только, что ответа пока нет. Решает
    отдельная проверка - если не пройдёт и она, сервер и правда недоступен (`markServerUnreachable`).
  */
  if (timedOut) {
    const now = Date.now();
    if (verifyReach && now - lastVerifyAt >= VERIFY_THROTTLE_MS) {
      lastVerifyAt = now;
      verifyReach();
    }
    return;
  }

  lastFailureAt = Date.now();
  failuresInRow += 1;
  if (failuresInRow < UNREACHABLE_AFTER_FAILURES) {
    return;
  }
  markServerUnreachable();
};

/**
 * Проверка связи не прошла - сервер недоступен, и это окончательный ответ.
 *
 * Зовётся из двух мест: счёт провалов подключения (см. выше) и провалившаяся проверка. Второе и
 * есть то, ради чего проверка существует: у таймаута своего голоса больше нет.
 */
export const markServerUnreachable = (): void => {
  setServerUnreachable(true);
  if (onlineManager.isOnline()) {
    onlineManager.setOnline(false);
  }
};

/**
 * Сервер ответил - значит, сеть есть, что бы ни говорил флаг браузера.
 *
 * Пока react-query считает, что сети нет, запросы стоят на паузе и ждут события `online`. Если
 * флаг соврал и событие не придёт, пауза стала бы вечной. Поэтому любое доказательство живой сети
 * её снимает: ответ шлюза (мутации уходят и без сети, так что нажатая кнопка его даёт) и открытый
 * сокет (он переподключается по своему таймеру и react-query не спрашивает). Заодно снимается
 * "сервер недоступен", а переход react-query в "сеть есть" запускает `catchUp`.
 */
export const markNetworkProven = (): void => {
  failuresInRow = 0;
  lastFailureAt = null;
  setServerUnreachable(false);
  if (!onlineManager.isOnline()) {
    onlineManager.setOnline(true);
  }
};

/** Только для тестов: перепроверка связи снова разрешена прямо сейчас. */
export const resetVerifyThrottleForTests = (): void => {
  lastVerifyAt = 0;
};

/** Только для тестов: сервер снова считается доступным, счёт провалов с нуля. */
export const resetServerReachForTests = (): void => {
  failuresInRow = 0;
  lastFailureAt = null;
  serverUnreachable = false;
};
